// src/components/CorrelationPanel.tsx (2026-06-19 新設 / v2 コントロール下置き)
// 相関分析タブ: 入院実績指標の月次相関を psych::pairs.panels 風の散布図行列 (SPLOM) で表示。
//   - サンプル = サイドバーで指定した期間 (corrFrom〜corrTo) の 各月 1 点。
//   - 指標の選択は「グラフ下のコントロール」で (y軸/x軸 の区別なし):
//       選択中 = 散布図行列に出す指標 (順序=列順、 先頭🎯=相関の基準)。 ドラッグで並べ替え・×で除外。
//       追加 = カテゴリ別 (収入/行為別収入/入院実績/手術実績/経路) のチップをクリック or ドラッグ。
//       名前の横の数値 = 先頭(基準)指標との相関。 既定セットは DEFAULT_SEL。
//   - 変数ソース = 入院実績(budgetSpec) + 入院経路(経路_/経由_) + 手術(件数/予定/緊急/麻酔・全科合算) + 行為別(行為:小計)。
//   - 上三角 = 散布図 (+ 線形回帰線)、 下三角 = 相関係数 r (符号で色・|r|で大きさ + 有意性)、 対角 = 月次推移 (折れ線)。
//   - Pearson。 欠損 (NaN) は pairwise-complete。 GP/単価/薬剤費系は「予算未確定月=0」を欠損扱い。
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useFilters, monthShift } from "../store";
import { useAuth } from "../lib/useAuth";
import type { DeptMetricsAllResponse, MetricRow, PresetsResponse, Preset, SurgeryResponse, KbRevenueResponse } from "@shared/api";
import { resolveAllSpecs } from "./prototypes/budgetSpec";

interface Props { metrics: MetricRow[]; dateMax: string; }

const SKIP_ZERO_KEYS = new Set(["GP", "GP単価", "GP単価_在院", "出来高換算薬剤費", "出来高換算材料費", "出来高換算薬剤材料費"]);
// 入院経路 (fact_long_all 由来・dept_metrics_all data.rows に既に含まれる) の集計区分
const ROUTE_METRICS = [
  "経路_病院予定", "経路_病院緊急", "経路_総CL予定", "経路_総CL緊急",
  "経路_腎CL予定", "経路_腎CL緊急", "経路_地域ケア予定", "経路_地域ケア緊急",
  "経由_HP", "経由_CL", "経由_腎CL", "経由_地域ケア",
];
// 行為別 小計 (合計は入院収入と重複するので除外)
const KB_ACTS_X = [
  "入院料小計", "手術小計", "処置小計", "検査小計", "注射小計", "投薬小計",
  "画像診断小計", "医学管理", "食事小計", "自費小計", "その他小計",
];
// 新規入院数の予定/緊急 + 紹介入院数 (budgetSpec に無く fact_long_all data.rows から)
const EXTRA_ADM_METRICS: { key: string; label: string }[] = [
  { key: "予定入院数", label: "新規入院数(予定)" },
  { key: "緊急入院数", label: "新規入院数(緊急)" },
  { key: "紹介入院数", label: "紹介入院数" },
];
// 選択肢から外す指標
const EXCLUDE_KEYS = new Set(["予定入院率"]);
// 既定の選択中セット (順序保持・先頭=相関の基準)
const DEFAULT_SEL = [
  "入院収入", "入院単価", "入院患者延数", "病床利用率", "新規入院数",
  "予定入院数", "緊急入院数", "平均在院日数", "DPC期間2以内率", "手術:件数", "紹介入院数",
];
// カテゴリ分け (投入しやすくするための分類)
const CATEGORIES = ["収入", "行為別収入", "入院実績", "手術実績", "経路"] as const;
type Category = typeof CATEGORIES[number];
const REVENUE_KEYS = new Set(["入院収入", "GP", "入院単価", "在院単価", "GP単価", "GP単価_在院", "出来高換算薬剤材料費"]);
const ROUTE_KEYS = new Set([...ROUTE_METRICS, "紹介入院数", "紹介入院率"]);
function categoryOf(key: string): Category {
  if (key.startsWith("行為:")) return "行為別収入";
  if (key.startsWith("手術:")) return "手術実績";
  if (ROUTE_KEYS.has(key)) return "経路";
  if (REVENUE_KEYS.has(key)) return "収入";
  return "入院実績";
}

function monthDiff(a: string, b: string): number {
  const [ay, am] = a.split("-").map(Number);
  const [by, bm] = b.split("-").map(Number);
  return (by - ay) * 12 + (bm - am);
}

// ── 統計 ──────────────────────────────────────────────────────────────
function pearson(a: number[], b: number[]): { r: number | null; n: number } {
  let sx = 0, sy = 0, sxx = 0, syy = 0, sxy = 0, n = 0;
  for (let i = 0; i < a.length; i++) {
    const x = a[i], y = b[i];
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    sx += x; sy += y; sxx += x * x; syy += y * y; sxy += x * y; n++;
  }
  if (n < 3) return { r: null, n };
  const cov = sxy - sx * sy / n, vx = sxx - sx * sx / n, vy = syy - sy * sy / n;
  if (vx <= 0 || vy <= 0) return { r: null, n };
  return { r: cov / Math.sqrt(vx * vy), n };
}
function gammaln(x: number): number {
  const c = [76.18009172947146, -86.50532032941677, 24.01409824083091,
    -1.231739572450155, 0.1208650973866179e-2, -0.5395239384953e-5];
  let y = x, tmp = x + 5.5; tmp -= (x + 0.5) * Math.log(tmp);
  let ser = 1.000000000190015;
  for (let j = 0; j < 6; j++) { y += 1; ser += c[j] / y; }
  return -tmp + Math.log(2.5066282746310005 * ser / x);
}
function betacf(a: number, b: number, x: number): number {
  const FPMIN = 1e-30; const qab = a + b, qap = a + 1, qam = a - 1;
  let c = 1, d = 1 - qab * x / qap;
  if (Math.abs(d) < FPMIN) d = FPMIN; d = 1 / d; let h = d;
  for (let m = 1; m <= 200; m++) {
    const m2 = 2 * m;
    let aa = m * (b - m) * x / ((qam + m2) * (a + m2));
    d = 1 + aa * d; if (Math.abs(d) < FPMIN) d = FPMIN;
    c = 1 + aa / c; if (Math.abs(c) < FPMIN) c = FPMIN;
    d = 1 / d; h *= d * c;
    aa = -(a + m) * (qab + m) * x / ((a + m2) * (qap + m2));
    d = 1 + aa * d; if (Math.abs(d) < FPMIN) d = FPMIN;
    c = 1 + aa / c; if (Math.abs(c) < FPMIN) c = FPMIN;
    d = 1 / d; const del = d * c; h *= del;
    if (Math.abs(del - 1) < 3e-7) break;
  }
  return h;
}
function betai(a: number, b: number, x: number): number {
  if (x <= 0) return 0; if (x >= 1) return 1;
  const bt = Math.exp(gammaln(a + b) - gammaln(a) - gammaln(b) + a * Math.log(x) + b * Math.log(1 - x));
  return x < (a + 1) / (a + b + 2) ? bt * betacf(a, b, x) / a : 1 - bt * betacf(b, a, 1 - x) / b;
}
function corrP(r: number, n: number): number {
  if (n < 3 || !Number.isFinite(r)) return 1;
  if (Math.abs(r) >= 1) return 0;
  const df = n - 2, t = r * Math.sqrt(df / (1 - r * r));
  return betai(df / 2, 0.5, df / (df + t * t));
}
const stars = (p: number) => p < 0.001 ? "***" : p < 0.01 ? "**" : p < 0.05 ? "*" : "";

interface VarData { key: string; label: string; unit: string; vals: number[]; }

export function CorrelationPanel({ dateMax }: Props) {
  const f = useFilters();
  const corrTo = f.corrTo || dateMax;
  const corrFrom = f.corrFrom || monthShift(corrTo, -11);  // "" = 直近1年 (12ヶ月)
  const scopeLabel = f.focusedDept ?? "病院全体";
  const back = Math.max(11, Math.min(47, monthDiff(corrFrom, corrTo)));
  const [drag, setDrag] = useState<string | null>(null);
  const [overZone, setOverZone] = useState<"un" | "sel" | null>(null);
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const [pname, setPname] = useState("");
  const [pshared, setPshared] = useState(false);
  const [selPreset, setSelPreset] = useState<number | "">("");
  const [pmsg, setPmsg] = useState<string | null>(null);

  const { data: presetsData } = useQuery<PresetsResponse>({
    queryKey: ["presets", "correlation"],
    queryFn: () => api.presetsList("correlation"),
  });
  const presets = presetsData?.presets ?? [];

  const { data, isLoading, error } = useQuery<DeptMetricsAllResponse>({
    queryKey: ["dept_metrics_all", f.granularity, f.iryo, f.yotei, corrTo, corrFrom, f.focusedDept, back, "corr"],
    queryFn: () => api.deptMetricsAll({
      granularity: f.granularity, iryo: f.iryo, yotei: f.yotei,
      asOfMonth: corrTo, periodFrom: corrFrom,
      focusedDept: f.focusedDept ?? undefined, trendBack: back,
    }),
    placeholderData: (p) => p,
  });

  const resolved = useMemo(() => data ? resolveAllSpecs(data) : [], [data]);

  // 手術 (手術件数) と 行為別 (小計) も x/y に使えるよう別ソースを取得し月次で結合。
  const { data: surgData } = useQuery<SurgeryResponse>({
    queryKey: ["surgery", corrFrom, corrTo, "大分類", "corr"],
    queryFn: () => api.surgery({ periodFrom: corrFrom, periodTo: corrTo, dim: "大分類" }),
    placeholderData: (p) => p,
  });
  const { data: kbData } = useQuery<KbRevenueResponse>({
    queryKey: ["kb_revenue", corrFrom, corrTo, "corr"],
    queryFn: () => api.kbRevenue({ periodFrom: corrFrom, periodTo: corrTo }),
    placeholderData: (p) => p,
  });

  const { vars, months } = useMemo(() => {
    const base = (resolved[0]?.trend ?? []).map((p) => p.date).filter((d) => d >= corrFrom && d <= corrTo);
    const out: VarData[] = [];
    const valsFrom = (m: Map<string, number>, skip = false) => base.map((d) => {
      const v = m.get(d);
      if (v == null || !Number.isFinite(v)) return NaN;
      if (skip && v === 0) return NaN;
      return v;
    });
    const rowByMetric = new Map((data?.rows ?? []).map((r) => [r.集計区分, r]));
    const fromRow = (mk: string): Map<string, number> | null => {
      const row = rowByMetric.get(mk);
      if (!row) return null;
      const byDate = new Map<string, number>();
      row.trend.forEach((p) => { if (p.actual != null) byDate.set(p.date, p.actual); });
      return byDate;
    };
    // 1. 入院実績 (budgetSpec、 派生含む。 予定入院率 は除外)
    for (const r of resolved) {
      if (EXCLUDE_KEYS.has(r.spec.key)) continue;
      const byDate = new Map<string, number>();
      r.trend.forEach((p) => { if (p.actual != null) byDate.set(p.date, p.actual); });
      out.push({ key: r.spec.key, label: r.spec.label, unit: r.spec.unit, vals: valsFrom(byDate, SKIP_ZERO_KEYS.has(r.spec.key)) });
    }
    // 1b. 新規入院数(予定/緊急) + 紹介入院数 (data.rows から)
    for (const em of EXTRA_ADM_METRICS) {
      const byDate = fromRow(em.key);
      if (byDate) out.push({ key: em.key, label: em.label, unit: "人", vals: valsFrom(byDate) });
    }
    // 2. 入院経路 (dept_metrics_all data.rows に既にある 経路_/経由_)
    for (const mk of ROUTE_METRICS) {
      const byDate = fromRow(mk);
      if (byDate) out.push({ key: mk, label: mk.replace("_", ":"), unit: "人", vals: valsFrom(byDate) });
    }
    // 3. 手術タブの実績 (件数/予定/緊急/全身麻酔/局所麻酔)。
    //    手術 API は総合計行を返さないため、 科フォーカス無し(or 不一致)時は全科を月次合算して全院値に。
    if (surgData?.rows?.length) {
      const depts = new Set(surgData.rows.map((r) => r.診療科));
      const onDept = f.focusedDept && depts.has(f.focusedDept) ? f.focusedDept : null;
      const suffix = onDept ? "" : "(全院)";
      const SURG_METRICS: { col: string; label: string }[] = [
        { col: "手術件数", label: "件数" }, { col: "予定", label: "予定" }, { col: "緊急", label: "緊急" },
        { col: "全麻", label: "全身麻酔" }, { col: "局麻", label: "局所麻酔" },
      ];
      for (const sm of SURG_METRICS) {
        const m = new Map<string, number>();
        for (const r of surgData.rows) {
          if (r.集計区分 !== sm.col) continue;
          if (onDept && r.診療科 !== onDept) continue;
          // 2026-07-17: null ガード (手術系=加算指標なので現状 null は来ないが、
          //   Number(null)===0 の暗黙加算は事故の温床なので明示的に弾く)
          if (r.実績 != null) m.set(r.date, (m.get(r.date) ?? 0) + Number(r.実績));  // onDept 無し = 全科合算
        }
        if (m.size) out.push({ key: `手術:${sm.label}`, label: `手術:${sm.label}${suffix}`, unit: "件", vals: valsFrom(m) });
      }
    }
    // 4. 行為別 小計 (科一致すればその科、 無ければ総合計)
    if (kbData?.rows?.length) {
      const depts = new Set(kbData.rows.map((r) => r.診療科));
      const kd = (f.focusedDept && depts.has(f.focusedDept)) ? f.focusedDept : "総合計";
      const byAct = new Map<string, Map<string, number>>();
      for (const r of kbData.rows) if (r.診療科 === kd) {
        if (!byAct.has(r.集計区分)) byAct.set(r.集計区分, new Map());
        // 2026-07-17: 実績 は null がありうる (分母0 の比指標)。Number(null)===0 で
        //   0 セルに化けるのを防ぐ。Map に入れなければ get() が undefined → "—" 表示。
        if (r.実績 != null) byAct.get(r.集計区分)!.set(r.date, Number(r.実績));
      }
      for (const act of KB_ACTS_X) {
        const m = byAct.get(act);
        if (m?.size) out.push({ key: `行為:${act}`, label: `行為:${act}`, unit: "千円", vals: valsFrom(m) });
      }
    }
    return { vars: out, months: base };
  }, [resolved, data, surgData, kbData, corrFrom, corrTo, f.focusedDept]);

  const byKey = useMemo(() => new Map(vars.map((v) => [v.key, v])), [vars]);

  // 選択中: corrSel (csv) 優先、 空なら既定セット。 存在する key のみ・順序保持・重複除去。
  const selectedSel = useMemo(() => {
    const raw = f.corrSel ? f.corrSel.split(",") : DEFAULT_SEL;
    const ks = raw.filter((k) => byKey.has(k));
    return ks.filter((k, i) => ks.indexOf(k) === i);
  }, [f.corrSel, byKey]);
  const selectedSet = useMemo(() => new Set(selectedSel), [selectedSel]);
  const refVar = selectedSel.length ? byKey.get(selectedSel[0]) : undefined;  // 先頭=相関の基準

  // 各指標の「先頭指標との相関」 (名前横の数値・カテゴリ内の並べ替え用)
  const corrWithRef = useMemo(() => {
    const m = new Map<string, number | null>();
    if (refVar) for (const v of vars) m.set(v.key, pearson(refVar.vals, v.vals).r);
    return m;
  }, [vars, refVar]);

  // SPLOM 対象 = selectedSel 順
  const ordered = useMemo(() => selectedSel.map((k) => byKey.get(k)).filter((v): v is VarData => !!v), [selectedSel, byKey]);

  const N = ordered.length;
  const mat = useMemo(() => {
    const m: { r: number | null; n: number; p: number }[][] = [];
    for (let i = 0; i < N; i++) { m[i] = [];
      for (let j = 0; j < N; j++) {
        const { r, n } = pearson(ordered[i].vals, ordered[j].vals);
        m[i][j] = { r, n, p: r != null ? corrP(r, n) : 1 };
      } }
    return m;
  }, [ordered, N]);

  const ranges = useMemo(() => ordered.map((v) => {
    const fin = v.vals.filter(Number.isFinite);
    if (fin.length === 0) return { min: 0, max: 1, span: 1 };  // 全欠損ガード
    const min = Math.min(...fin), max = Math.max(...fin);
    return { min, max, span: (max - min) || 1 };
  }), [ordered]);

  // ── 選択中の編集 (クリック追加 / DnD) ─────────────────────────
  const commit = (list: string[]) => f.setCorrSel(list.filter((k, i, a) => a.indexOf(k) === i).join(","));
  const addSel = (key: string, beforeKey?: string) => {
    const cur = selectedSel.filter((k) => k !== key);
    if (beforeKey && beforeKey !== key) {
      const idx = cur.indexOf(beforeKey);
      cur.splice(idx < 0 ? cur.length : idx, 0, key);
    } else cur.push(key);
    commit(cur);
  };
  const removeSel = (key: string) => commit(selectedSel.filter((k) => k !== key));

  // ── 条件セット (presets) ─────────────────────────────────────
  const loadPreset = (p: Preset) => {
    const c = p.config;
    if (!c) return;
    // 新形式 sel、 旧形式は [y, ...x] に変換
    const sel = c.sel ?? (c.y ? [c.y, ...(c.x ?? [])] : []);
    useFilters.setState({
      corrSel: sel.join(","),
      corrFrom: c.from || "2024-04-01", corrTo: c.to ?? "",
      granularity: c.gran, focusedDept: c.dept ?? null,
    });
    setSelPreset(p.id);
    setPmsg(`「${p.name}」を適用`);
  };
  const savePreset = async () => {
    const name = pname.trim();
    if (!name) { setPmsg("名前を入力してください"); return; }
    try {
      await api.presetCreate({
        feature: "correlation", name, shared: isAdmin && pshared,
        config: { sel: selectedSel, from: corrFrom, to: f.corrTo, gran: f.granularity, dept: f.focusedDept },
      });
      setPname(""); setPshared(false);
      await qc.invalidateQueries({ queryKey: ["presets", "correlation"] });
      setPmsg(`「${name}」を保存${isAdmin && pshared ? " (全ユーザー共有)" : ""}`);
    } catch (e) { setPmsg(`保存失敗: ${(e as Error).message}`); }
  };
  const deletePreset = async (p: Preset) => {
    if (!window.confirm(`条件セット「${p.name}」を削除しますか?`)) return;
    try {
      await api.presetDelete(p.id);
      if (selPreset === p.id) setSelPreset("");
      await qc.invalidateQueries({ queryKey: ["presets", "correlation"] });
      setPmsg(`「${p.name}」を削除`);
    } catch (e) { setPmsg(`削除失敗: ${(e as Error).message}`); }
  };
  const selectedPreset = presets.find((p) => p.id === selPreset);

  // ── レイアウト ───────────────────────────────────────────────
  const cell = Math.max(54, Math.min(96, Math.round(1120 / Math.max(1, N))));
  // ラベルが長いと上(回転列ラベル)/左(行ラベル)/右(末列ラベル)で見切れるので、 余白を確保し
  // 長いラベルは省略 (full は <title> hover)。
  const trunc = (s: string) => s.length > 14 ? s.slice(0, 14) + "…" : s;
  const ML = 178, MT = 150, MR = 112;
  const W = ML + N * cell + MR, H = MT + N * cell;
  const pad = Math.max(5, Math.round(cell * 0.12));
  const refLabel = refVar?.label ?? "—";
  const rFmt = (r: number | null | undefined) => r == null ? "—" : r.toFixed(2);
  const rColor = (r: number | null | undefined) => r == null ? "#94a3b8" : r >= 0 ? "#1d4ed8" : "#dc2626";

  const myPresets = presets.filter((p) => p.mine && !p.isShared);
  const sharedPresets = presets.filter((p) => p.isShared);
  // 他ユーザーの private (= サーバが管理者にだけ返す)
  const othersPrivate = presets.filter((p) => !p.mine && !p.isShared);

  return (
    <div className="space-y-3">
      {/* 条件セット ツールバー */}
      <section className="card p-3">
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <span className="font-semibold text-slate-600">💾 条件セット</span>
          <select value={selPreset}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "") { setSelPreset(""); return; }
              const p = presets.find((x) => x.id === Number(v));
              if (p) loadPreset(p);
            }}
            className="border border-slate-300 rounded px-2 py-1 bg-white min-w-[180px]">
            <option value="">— 読み込み —</option>
            {myPresets.length > 0 && (
              <optgroup label="自分の条件セット">
                {myPresets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </optgroup>
            )}
            {sharedPresets.length > 0 && (
              <optgroup label="共有 (管理者作成)">
                {sharedPresets.map((p) => <option key={p.id} value={p.id}>🌐 {p.name}{p.mine ? "" : ` (${p.ownerEmail.split("@")[0]})`}</option>)}
              </optgroup>
            )}
            {othersPrivate.length > 0 && (
              <optgroup label="他ユーザー (管理者表示)">
                {othersPrivate.map((p) => <option key={p.id} value={p.id}>👤 {p.name} ({p.ownerEmail.split("@")[0]})</option>)}
              </optgroup>
            )}
          </select>
          {selectedPreset?.canDelete && (
            <button type="button" onClick={() => selectedPreset && deletePreset(selectedPreset)}
              className="px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50" title="この条件セットを削除">🗑 削除</button>
          )}
          <span className="mx-1 text-slate-300">|</span>
          <input value={pname} onChange={(e) => setPname(e.target.value)} placeholder="新規セット名" maxLength={80}
            className="border border-slate-300 rounded px-2 py-1 bg-white w-36" />
          {isAdmin && (
            <label className="flex items-center gap-1 text-slate-600 select-none">
              <input type="checkbox" checked={pshared} onChange={(e) => setPshared(e.target.checked)} />
              全ユーザーに共有
            </label>
          )}
          <button type="button" onClick={savePreset}
            className="px-3 py-1 rounded bg-emerald-600 text-white font-semibold hover:bg-emerald-700">保存</button>
          {pmsg && <span className="text-emerald-700">{pmsg}</span>}
          <span className="ml-auto text-[10px] text-slate-400">
            条件セット = y/x/期間/粒度/診療科。 {isAdmin ? "「共有」で全ユーザーに公開可。" : "保存はあなた専用。"}
          </span>
        </div>
      </section>

      <section className="card p-4">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <h2 className="text-base font-semibold">🔗 相関分析 — {scopeLabel}</h2>
          <span className="perf-pill bg-amber-100 text-amber-800 border-amber-200">選択中 {ordered.length} 指標 (基準: {refLabel})</span>
          <span className="perf-pill">{months.length} ヶ月 ({corrFrom.slice(0, 7)} 〜 {corrTo.slice(0, 7)})</span>
          {data && <span className="perf-pill">SQL {data.sqlMs} ms</span>}
          {isLoading && <span className="text-xs text-slate-400">loading…</span>}
        </div>
        <div className="text-xs text-slate-500 leading-tight">
          各点 = 1 ヶ月 (n={months.length})。 列 = 下のパネルで選んだ指標 (1列目=相関の基準)。 上三角=散布図+回帰線 /
          下三角=相関 r (青正/赤負・文字 ∝ |r|・<b>*</b>p&lt;.05 <b>**</b>p&lt;.01 <b>***</b>p&lt;.001) / 対角=月次推移(折れ線)。
          Pearson・欠損 pairwise 除外。
        </div>
        {error && <div className="text-sm text-red-700 bg-red-50 p-2 rounded mt-2">取得エラー: {(error as Error).message}</div>}
      </section>

      <section className="card p-3 overflow-auto">
        {N < 2 || months.length < 3 ? (
          <div className="text-xs text-slate-400 py-10 text-center">
            指標を 2 つ以上選択してください (選択中 {selectedSel.length} / サンプル {months.length} ヶ月)。
          </div>
        ) : (
          <svg width={W} height={H} style={{ fontFamily: "var(--jp-font, system-ui)" }} className="select-none">
            {ordered.map((v, j) => (
              <text key={`ct${j}`} x={ML + j * cell + cell / 2} y={MT - 6}
                    transform={`rotate(-40 ${ML + j * cell + cell / 2} ${MT - 6})`}
                    textAnchor="start" fontSize={11} fontWeight={j === 0 ? 700 : 400}
                    fill={j === 0 ? "#b45309" : "#334155"}><title>{v.label}</title>{trunc(v.label)}</text>
            ))}
            {ordered.map((v, i) => (
              <text key={`rt${i}`} x={ML - 6} y={MT + i * cell + cell / 2}
                    textAnchor="end" dominantBaseline="middle" fontSize={11}
                    fontWeight={i === 0 ? 700 : 400} fill={i === 0 ? "#b45309" : "#334155"}><title>{v.label}</title>{trunc(v.label)}</text>
            ))}
            <rect x={ML} y={MT} width={cell} height={N * cell} fill="#fef3c7" opacity={0.45} />
            <rect x={ML} y={MT} width={N * cell} height={cell} fill="#fef3c7" opacity={0.45} />
            {ordered.map((_, i) =>
              ordered.map((__, j) => {
                const x0 = ML + j * cell, y0 = MT + i * cell, key = `c${i}-${j}`;
                if (i === j) {
                  // 対角 = その指標の月次推移 (折れ線)。 欠損 (NaN) は線を切る。
                  const rg = ranges[i], v = ordered[i].vals, nn = v.length;
                  let d = "", pen = false;
                  for (let k = 0; k < nn; k++) {
                    if (!Number.isFinite(v[k])) { pen = false; continue; }
                    const xx = x0 + pad + (nn > 1 ? k / (nn - 1) : 0.5) * (cell - 2 * pad);
                    const yy = y0 + cell - pad - ((v[k] - rg.min) / rg.span) * (cell - 2 * pad);
                    d += `${pen ? " L" : " M"}${xx.toFixed(1)} ${yy.toFixed(1)}`;
                    pen = true;
                  }
                  return (
                    <g key={key}>
                      <rect x={x0} y={y0} width={cell} height={cell} fill="#f8fafc" stroke="#e2e8f0" />
                      {d && <path d={d} fill="none" stroke={i === 0 ? "#f59e0b" : "#0f766e"} strokeWidth={1.5} />}
                    </g>
                  );
                }
                if (i > j) {
                  // 下三角 = 相関係数 (2026-06-19: 上下を反転。 上三角=散布図 / 下三角=相関)
                  const c = mat[i][j];
                  if (c.r == null) return <g key={key}><rect x={x0} y={y0} width={cell} height={cell} fill="#fff" stroke="#f1f5f9" /></g>;
                  const fs = 11 + Math.min(1, Math.abs(c.r)) * 16;
                  // 一覧性のため |r| で背景を淡くヒートマップ (青=正 / 赤=負)
                  const tint = c.r >= 0
                    ? `rgba(37,99,235,${(Math.abs(c.r) * 0.22).toFixed(3)})`
                    : `rgba(220,38,38,${(Math.abs(c.r) * 0.22).toFixed(3)})`;
                  return (
                    <g key={key}>
                      <rect x={x0} y={y0} width={cell} height={cell} fill={tint} stroke="#f1f5f9" />
                      <text x={x0 + cell / 2} y={y0 + cell / 2} textAnchor="middle" dominantBaseline="middle"
                            fontSize={fs} fontWeight={700} fill={rColor(c.r)}>{c.r.toFixed(2)}</text>
                      <text x={x0 + cell / 2} y={y0 + cell / 2 + fs * 0.7} textAnchor="middle" dominantBaseline="middle"
                            fontSize={10} fill={rColor(c.r)}>{stars(c.p)}</text>
                    </g>
                  );
                }
                const rx = ranges[j], ry = ranges[i];
                const px = (v: number) => x0 + pad + ((v - rx.min) / rx.span) * (cell - 2 * pad);
                const py = (v: number) => y0 + cell - pad - ((v - ry.min) / ry.span) * (cell - 2 * pad);
                const pts: [number, number][] = [];
                for (let k = 0; k < months.length; k++) {
                  const xv = ordered[j].vals[k], yv = ordered[i].vals[k];
                  if (Number.isFinite(xv) && Number.isFinite(yv)) pts.push([xv, yv]);
                }
                let line: { x1: number; y1: number; x2: number; y2: number } | null = null;
                if (pts.length >= 3) {
                  const n = pts.length; let sx = 0, sy = 0, sxx = 0, sxy = 0;
                  pts.forEach(([xv, yv]) => { sx += xv; sy += yv; sxx += xv * xv; sxy += xv * yv; });
                  const den = n * sxx - sx * sx;
                  if (den !== 0) { const b = (n * sxy - sx * sy) / den, a = (sy - b * sx) / n;
                    line = { x1: px(rx.min), y1: py(a + b * rx.min), x2: px(rx.max), y2: py(a + b * rx.max) }; }
                }
                const inY = i === 0 || j === 0;
                return (
                  <g key={key}>
                    <rect x={x0} y={y0} width={cell} height={cell} fill={inY ? "#fffbeb" : "#fff"} stroke="#e2e8f0" />
                    {pts.map(([xv, yv], k) => <circle key={k} cx={px(xv)} cy={py(yv)} r={1.8} fill="#0f766e" fillOpacity={0.75} />)}
                    {line && <line x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2} stroke="#dc2626" strokeWidth={1.2} strokeOpacity={0.8} />}
                  </g>
                );
              })
            )}
          </svg>
        )}
      </section>

      {/* ── 指標の選択 (選択中 + カテゴリ を表で横並び) ── */}
      <section className="card p-3">
        <div className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-2 flex-wrap">
          指標の選択 — 「選択中」が散布図行列 (先頭🎯=相関の基準・ドラッグで並べ替え)。 各カテゴリの指標をクリック(orドラッグ)で追加 / ×で除外。 数値=先頭指標との相関
          <button type="button" onClick={() => f.setCorrSel("")}
            className="text-[11px] font-normal px-2 py-0.5 rounded border border-slate-200 text-slate-500 hover:bg-slate-50">↺ 既定に戻す</button>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {/* 選択中 (表) */}
          <div onDragOver={(e) => { e.preventDefault(); setOverZone("sel"); }}
            onDrop={() => { if (drag) addSel(drag); setDrag(null); setOverZone(null); }}
            className={`shrink-0 w-52 border rounded overflow-hidden ${overZone === "sel" ? "border-emerald-400 ring-2 ring-emerald-200" : "border-emerald-300"}`}>
            <div className="text-[11px] font-bold text-emerald-800 bg-emerald-100 px-2 py-1">選択中 ({selectedSel.length})</div>
            <div className="max-h-80 overflow-y-auto">
              {selectedSel.map((k, i) => {
                const v = byKey.get(k); if (!v) return null;
                const r = corrWithRef.get(k);
                return (
                  <div key={k} draggable onDragStart={() => setDrag(k)} onDragEnd={() => { setDrag(null); setOverZone(null); }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.stopPropagation(); if (drag) addSel(drag, k); setDrag(null); setOverZone(null); }}
                    title={v.label}
                    className={`flex items-center gap-1 text-xs px-2 py-1 border-b border-slate-100 cursor-grab ${i === 0 ? "bg-amber-50 font-semibold" : "hover:bg-emerald-50"}`}>
                    <span className="shrink-0">{i === 0 ? "🎯" : "⠿"}</span>
                    <span className="truncate flex-1">{v.label}</span>
                    {i > 0 && <span className="tabular-nums font-semibold shrink-0" style={{ color: rColor(r) }}>{rFmt(r)}</span>}
                    <button type="button" onClick={() => removeSel(k)} className="text-slate-400 hover:text-red-600 shrink-0">×</button>
                  </div>
                );
              })}
              {selectedSel.length === 0 && <div className="text-[11px] text-slate-400 px-2 py-3">← 右の表から追加</div>}
            </div>
          </div>
          {/* カテゴリ各表 */}
          {CATEGORIES.map((cat) => {
            const items = vars.filter((v) => !selectedSet.has(v.key) && categoryOf(v.key) === cat)
              .sort((a, b) => Math.abs(corrWithRef.get(b.key) ?? 0) - Math.abs(corrWithRef.get(a.key) ?? 0));
            if (items.length === 0) return null;  // 全選択済みカテゴリは列を出さない (再追加時に再表示)
            return (
              <div key={cat} className="shrink-0 w-48 border border-slate-200 rounded overflow-hidden">
                <div className="text-[11px] font-bold text-slate-600 bg-slate-100 px-2 py-1">{cat} ({items.length})</div>
                <div className="max-h-80 overflow-y-auto">
                  {items.map((v) => {
                    const r = corrWithRef.get(v.key);
                    return (
                      <button key={v.key} type="button" draggable
                        onDragStart={() => setDrag(v.key)} onDragEnd={() => setDrag(null)}
                        onClick={() => addSel(v.key)} title={v.label}
                        className="w-full flex items-center gap-1 text-xs px-2 py-1 border-b border-slate-100 hover:bg-blue-50 cursor-pointer text-left">
                        <span className="truncate flex-1">{v.label}</span>
                        {r != null && <span className="tabular-nums shrink-0" style={{ color: rColor(r) }}>{rFmt(r)}</span>}
                      </button>
                    );
                  })}
                  {items.length === 0 && <div className="text-[11px] text-slate-300 px-2 py-3">なし</div>}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
